import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import adminApi from '../utils/adminApi';
import { formatCurrency } from '../utils/formatters';

// QR Modal Component with QR code image
const QRModal = ({ qr, onClose, getStatusBadge, formatDate }) => {
  const [qrImageUrl, setQrImageUrl] = useState('');

  useEffect(() => {
    if (qr?.code) {
      QRCode.toDataURL(qr.code, {
        width: 250,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'H'
      }).then(setQrImageUrl).catch(console.error);
    }
  }, [qr?.code]);

  const openFullPage = () => {
    window.open(`/qr/${encodeURIComponent(qr.code)}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/qr/${encodeURIComponent(qr.code)}`);
    alert('Link copied!');
  };

  return (
    <div className="qr-details-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>

        {/* QR Code Image */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {qrImageUrl ? (
            <img
              src={qrImageUrl}
              alt="QR Code"
              style={{ maxWidth: '250px', border: '1px solid #333', borderRadius: '8px' }}
            />
          ) : (
            <div style={{ width: '250px', height: '250px', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', borderRadius: '8px' }}>
              Loading...
            </div>
          )}
        </div>

        {/* Type & Value Header */}
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          <span className={`type-badge type-${qr.type?.toLowerCase()}`} style={{ fontSize: '16px', padding: '8px 16px' }}>
            {qr.type}
          </span>
          {qr.value && (
            <div style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '10px', color: qr.value > 0 ? '#4caf50' : '#f44336' }}>
              {qr.value > 0 ? '+' : ''}{formatCurrency(qr.value)}
            </div>
          )}
        </div>

        {/* Status */}
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          {getStatusBadge(qr)}
        </div>

        {/* Code String */}
        <div style={{ background: '#1a1a2e', padding: '10px', borderRadius: '6px', marginBottom: '15px', textAlign: 'center' }}>
          <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>{qr.code}</code>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
          <button
            onClick={openFullPage}
            style={{ padding: '10px 20px', background: '#2196f3', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            🔗 Open Full Page
          </button>
          <button
            onClick={copyLink}
            style={{ padding: '10px 20px', background: '#9c27b0', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            📋 Copy Link
          </button>
          <button
            onClick={() => window.print()}
            style={{ padding: '10px 20px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            🖨️ Print
          </button>
        </div>

        {/* Details */}
        <div className="qr-details">
          {qr.used_by && (
            <>
              <div className="detail-row">
                <label>Used By:</label>
                <span>{qr.username || qr.user_short_id}</span>
              </div>
              <div className="detail-row">
                <label>Used At:</label>
                <span>{formatDate(qr.used_at)}</span>
              </div>
            </>
          )}
          <div className="detail-row">
            <label>Created:</label>
            <span>{formatDate(qr.created_at)}</span>
          </div>
          {qr.expires_at && (
            <div className="detail-row">
              <label>Expires:</label>
              <span>{formatDate(qr.expires_at)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminQRCodeTab = () => {
  const [qrCodes, setQrCodes] = useState({
    registration: [],
    bonus: [],
    minigame: []
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, used, available
  const [typeFilter, setTypeFilter] = useState('all'); // all, registration, bonus, minigame
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [selectedQR, setSelectedQR] = useState(null);

  useEffect(() => {
    fetchQRCodeData();
    // Refresh every 10 seconds
    const interval = setInterval(fetchQRCodeData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchQRCodeData = async () => {
    try {
      const response = await adminApi.get('/api/admin/qr-codes');
      setQrCodes(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching QR code data:', err);
      setStatusMessage({
        text: 'Failed to fetch QR code data',
        type: 'error'
      });
      setLoading(false);
    }
  };

  const generateNewQRCodes = async (type, count) => {
    try {
      setStatusMessage({ text: `Generating ${count} ${type} QR codes...`, type: 'info' });

      const response = await adminApi.post('/api/admin/qr-codes/generate', {
        type,
        count
      });

      if (response.data.success) {
        setStatusMessage({
          text: `Successfully generated ${count} ${type} QR codes`,
          type: 'success'
        });
        fetchQRCodeData(); // Refresh the list
      }

      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
    } catch (err) {
      console.error('Error generating QR codes:', err);
      setStatusMessage({
        text: 'Failed to generate QR codes',
        type: 'error'
      });
    }
  };

  const deleteQRCode = async (code) => {
    if (!window.confirm(`Are you sure you want to delete QR code ${code}?`)) {
      return;
    }

    try {
      const response = await adminApi.delete(`/api/admin/qr-codes/${code}`);

      if (response.data.success) {
        setStatusMessage({
          text: `Deleted QR code ${code}`,
          type: 'success'
        });
        fetchQRCodeData();
      }

      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
    } catch (err) {
      console.error('Error deleting QR code:', err);
      setStatusMessage({
        text: 'Failed to delete QR code',
        type: 'error'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (qr) => {
    if (qr.used) {
      return <span className="badge badge-used">USED</span>;
    }
    if (qr.expires_at && new Date(qr.expires_at) < new Date()) {
      return <span className="badge badge-expired">EXPIRED</span>;
    }
    return <span className="badge badge-available">AVAILABLE</span>;
  };

  const filterQRCodes = () => {
    let allCodes = [];

    // Combine all QR codes based on type filter
    if (typeFilter === 'all' || typeFilter === 'registration') {
      allCodes = [...allCodes, ...qrCodes.registration.map(qr => ({ ...qr, type: 'REGISTRATION' }))];
    }
    if (typeFilter === 'all' || typeFilter === 'bonus') {
      allCodes = [...allCodes, ...qrCodes.bonus.map(qr => ({ ...qr, type: 'BONUS' }))];
    }
    if (typeFilter === 'all' || typeFilter === 'minigame') {
      allCodes = [...allCodes, ...qrCodes.minigame.map(qr => ({ ...qr, type: 'MINIGAME' }))];
    }

    // Apply status filter
    if (filter === 'used') {
      allCodes = allCodes.filter(qr => qr.used);
    } else if (filter === 'available') {
      allCodes = allCodes.filter(qr => !qr.used);
    }

    return allCodes;
  };

  const filteredCodes = filterQRCodes();

  // Calculate statistics
  const stats = {
    registration: {
      total: qrCodes.registration.length,
      used: qrCodes.registration.filter(qr => qr.used).length,
      available: qrCodes.registration.filter(qr => !qr.used).length
    },
    bonus: {
      total: qrCodes.bonus.length,
      used: qrCodes.bonus.filter(qr => qr.used).length,
      available: qrCodes.bonus.filter(qr => !qr.used).length
    },
    minigame: {
      total: qrCodes.minigame.length,
      used: qrCodes.minigame.filter(qr => qr.used).length,
      available: qrCodes.minigame.filter(qr => !qr.used).length
    }
  };

  if (loading) {
    return <div className="loading">Loading QR code data...</div>;
  }

  return (
    <div className="admin-qr-codes-tab">
      {statusMessage.text && (
        <div className={`status-message ${statusMessage.type}`}>
          {statusMessage.text}
        </div>
      )}

      {/* Statistics Overview */}
      <div className="qr-stats-overview">
        <h3>QR Code Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <h4>📝 Registration Codes</h4>
            <div className="stat-details">
              <p>Total: <strong>{stats.registration.total}</strong></p>
              <p>Used: <strong className="text-used">{stats.registration.used}</strong></p>
              <p>Available: <strong className="text-available">{stats.registration.available}</strong></p>
            </div>
          </div>
          <div className="stat-card">
            <h4>💰 Bonus/Penalty Codes</h4>
            <div className="stat-details">
              <p>Total: <strong>{stats.bonus.total}</strong></p>
              <p>Used: <strong className="text-used">{stats.bonus.used}</strong></p>
              <p>Available: <strong className="text-available">{stats.bonus.available}</strong></p>
            </div>
          </div>
          <div className="stat-card">
            <h4>🎮 Minigame Codes</h4>
            <div className="stat-details">
              <p>Total: <strong>{stats.minigame.total}</strong></p>
              <p>Used: <strong className="text-used">{stats.minigame.used}</strong></p>
              <p>Available: <strong className="text-available">{stats.minigame.available}</strong></p>
            </div>
          </div>
        </div>
      </div>

      {/* Generate New QR Codes Section */}
      <div className="qr-generation-section">
        <h3>Generate New QR Codes</h3>
        <div className="generation-controls">
          <button
            onClick={() => generateNewQRCodes('REGISTRATION', 10)}
            className="generate-button"
          >
            Generate 10 Registration Codes
          </button>
          <button
            onClick={() => generateNewQRCodes('BONUS', 20)}
            className="generate-button"
          >
            Generate 20 Bonus Codes
          </button>
          <button
            onClick={() => generateNewQRCodes('MINIGAME', 5)}
            className="generate-button"
          >
            Generate 5 Minigame Codes
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="qr-filters">
        <h3>Filter QR Codes</h3>
        <div className="filter-controls">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="registration">Registration Only</option>
            <option value="bonus">Bonus/Penalty Only</option>
            <option value="minigame">Minigame Only</option>
          </select>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="used">Used Only</option>
            <option value="available">Available Only</option>
          </select>
          <button
            onClick={fetchQRCodeData}
            className="refresh-button"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* QR Codes Table */}
      <div className="qr-codes-table-section">
        <h3>QR Codes List ({filteredCodes.length} codes)</h3>
        <table className="qr-codes-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Status</th>
              <th>Value/Effect</th>
              <th>Used By</th>
              <th>Used At</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCodes.map((qr) => (
              <tr
                key={qr.code}
                className={qr.used ? 'used-row' : 'available-row'}
                onClick={() => setSelectedQR(qr)}
                style={{ cursor: 'pointer' }}
              >
                <td className="code-cell">
                  <code>{qr.code}</code>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(qr.code);
                      setStatusMessage({ text: 'Code copied!', type: 'success' });
                      setTimeout(() => setStatusMessage({ text: '', type: '' }), 2000);
                    }}
                    className="copy-btn"
                    title="Copy code"
                  >
                    📋
                  </button>
                </td>
                <td>
                  <span className={`type-badge type-${qr.type.toLowerCase()}`}>
                    {qr.type}
                  </span>
                </td>
                <td>{getStatusBadge(qr)}</td>
                <td>
                  {qr.type === 'REGISTRATION' && 'New Account'}
                  {qr.type === 'BONUS' && qr.value && (
                    <span className={qr.value > 0 ? 'text-positive' : 'text-negative'}>
                      {formatCurrency(qr.value)}
                    </span>
                  )}
                  {qr.type === 'MINIGAME' && (qr.minigame_type || 'Random Game')}
                </td>
                <td>
                  {qr.used_by ? (
                    <span className="user-link">
                      {qr.username || qr.user_short_id || qr.used_by}
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>{qr.used_at ? formatDate(qr.used_at) : '-'}</td>
                <td>{formatDate(qr.created_at)}</td>
                <td>
                  {!qr.used && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQRCode(qr.code);
                      }}
                      className="delete-btn"
                      title="Delete QR code"
                    >
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCodes.length === 0 && (
          <div className="no-codes">No QR codes match the selected filters</div>
        )}
      </div>

      {/* Selected QR Details Modal */}
      {selectedQR && (
        <QRModal
          qr={selectedQR}
          onClose={() => setSelectedQR(null)}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

export default AdminQRCodeTab;